import { rpcClient } from '@/api/rpc/client';
import { waitForBackgroundJob } from '@/api/background-jobs/waitForBackgroundJob';

/** What the caller tells the user once the file is on its way. */
export interface FileExportSummary {
  rowCount: number;
  truncated: boolean;
  filename: string;
}

/**
 * Collect a server-side file export: wait for its background job, then hand
 * the browser a short-lived signed link to the file in blob storage.
 *
 * The file never passes through the API — the browser downloads it straight
 * from storage, which serves it as an attachment under its filename. Throws
 * with the job's own message when it failed, so callers can show it as-is.
 */
export async function downloadFileExport(
  backgroundJobId: string,
): Promise<FileExportSummary> {
  const outcome = await waitForBackgroundJob(backgroundJobId);
  if (outcome.outcome === 'failed') {
    throw new Error(outcome.errorMessage ?? 'The export failed.');
  }
  if (outcome.outcome === 'vanished') {
    throw new Error('The export is no longer available.');
  }

  const file = await rpcClient.core.fileExports.getDownloadUrl({
    backgroundJobId,
  });
  const link = document.createElement('a');
  link.href = file.url;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  return {
    rowCount: file.rowCount,
    truncated: file.truncated,
    filename: file.filename,
  };
}
